/** Presentation helpers. Every one of them exists because the raw value is
 * unreadable, not because it is ugly. */

const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });

const UNITS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ["year", 365 * 24 * 3600],
  ["month", 30 * 24 * 3600],
  ["week", 7 * 24 * 3600],
  ["day", 24 * 3600],
  ["hour", 3600],
  ["minute", 60],
  ["second", 1],
];

/** "3 minutes ago", "in 2 days". */
export function relativeTime(iso: string | undefined | null): string {
  if (!iso) return "never";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "unknown";

  const deltaSeconds = (then - Date.now()) / 1000;
  const magnitude = Math.abs(deltaSeconds);

  if (magnitude < 45) return "just now";
  for (const [unit, seconds] of UNITS) {
    if (magnitude >= seconds) {
      return rtf.format(Math.round(deltaSeconds / seconds), unit);
    }
  }
  return "just now";
}

const absoluteFormatter = new Intl.DateTimeFormat(undefined, {
  dateStyle: "medium",
  timeStyle: "short",
});

/** "12 Jun 2026, 14:03" — used in tooltips and audit rows. */
export function absoluteTime(iso: string | undefined | null): string {
  if (!iso) return "—";
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "—" : absoluteFormatter.format(date);
}

/** Human byte sizes, base 1024, because that is what storage products quote. */
export function bytes(value: number): string {
  if (!Number.isFinite(value) || value < 0) return "—";
  const units = ["B", "KiB", "MiB", "GiB", "TiB", "PiB"];
  let index = 0;
  let scaled = value;
  while (scaled >= 1024 && index < units.length - 1) {
    scaled /= 1024;
    index += 1;
  }
  const digits = scaled < 10 && index > 0 ? 1 : 0;
  return `${scaled.toFixed(digits)} ${units[index]}`;
}


/** Turns an audit action such as "member.role_changed" into "Role changed". */
export function humaniseAction(action: string): string {
  const [, verb] = action.split(".");
  if (!verb) return action;
  const words = verb.replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** A device description from a user-agent string, good enough to recognise
 * your own laptop in a list of sessions. */
export function describeDevice(userAgent: string): string {
  if (!userAgent) return "Unknown device";

  const os =
    /Windows NT/.test(userAgent) ? "Windows"
    : /Mac OS X/.test(userAgent) ? "macOS"
    : /Android/.test(userAgent) ? "Android"
    : /(iPhone|iPad)/.test(userAgent) ? "iOS"
    : /Linux/.test(userAgent) ? "Linux"
    : "";

  const browser =
    /Edg\//.test(userAgent) ? "Edge"
    : /OPR\//.test(userAgent) ? "Opera"
    : /Firefox\//.test(userAgent) ? "Firefox"
    : /Chrome\//.test(userAgent) ? "Chrome"
    : /Safari\//.test(userAgent) ? "Safari"
    : "";

  if (browser && os) return `${browser} on ${os}`;
  return browser || os || userAgent.slice(0, 40);
}

/** Joins class names, dropping anything falsy. */
export function cx(...values: Array<string | false | null | undefined>): string {
  return values.filter(Boolean).join(" ");
}

/**
 * A number of seconds, as a person would say it.
 *
 * Builds take anything from four seconds to twenty minutes, and "1140s" is a
 * number somebody has to divide before it means anything.
 */
export function duration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "—";
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const minutes = Math.floor(seconds / 60);
  const rest = Math.round(seconds % 60);
  if (minutes < 60) return rest === 0 ? `${minutes}m` : `${minutes}m ${rest}s`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
}

/**
 * A time, short enough for a dense table.
 *
 * `relativeTime` writes "16 minutes ago", which is the right thing to read in a
 * sentence and too long for a column: the jobs table has three time columns and
 * all three clipped. This is the operator's form — 16m, 5h, 3d — and it belongs
 * with the exact value on hover, never alone.
 */
export function shortTime(iso: string | undefined | null): string {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return "—";

  const deltaSeconds = (Date.now() - then) / 1000;
  const ahead = deltaSeconds < 0;
  const seconds = Math.abs(deltaSeconds);

  const size =
    seconds < 45 ? "now"
    : seconds < 3600 ? `${Math.round(seconds / 60)}m`
    : seconds < 86400 ? `${Math.round(seconds / 3600)}h`
    : seconds < 2592000 ? `${Math.round(seconds / 86400)}d`
    : `${Math.round(seconds / 2592000)}mo`;

  if (size === "now") return "just now";
  return ahead ? `in ${size}` : `${size} ago`;
}

/**
 * How often something recurs, as a person would say it.
 *
 * `duration` is for how long something took and renders every component, which
 * is right for "3m 20s" and wrong for a schedule: an hourly job read "1h 0m"
 * and a daily retention window read "24h 0m". A cadence is one unit, because
 * that is how anybody says it out loud.
 */
export function cadence(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return "—";
  const units: [number, string][] = [
    [86400, "d"],
    [3600, "h"],
    [60, "m"],
  ];
  for (const [size, suffix] of units) {
    if (seconds >= size) {
      const whole = seconds / size;
      // Only when it divides cleanly. Ninety seconds is "90s", not "1.5m".
      if (Number.isInteger(whole)) return `${whole}${suffix}`;
    }
  }
  return `${Math.round(seconds)}s`;
}

/** A count, with thousands separators the reader recognises. */
export function number(value: number | string): string {
  const n = typeof value === "string" ? Number(value) : value;
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString();
}

/**
 * An image reference, short enough for a table cell.
 *
 * A reference pinned by digest is seventy-odd characters of which the last
 * sixty-four carry no information a person can use. Printed in full it does not
 * wrap — it is one unbroken token — so it forces its column wide and squeezes
 * every other column in the row into two and three lines each. That is what the
 * project's service table looked like.
 *
 * The first seven characters of a digest are what every tool shows and what
 * people actually compare, so that is what is kept. The full value belongs in a
 * title attribute, where somebody who needs it can still get at it.
 */
export function shortImage(reference: string | undefined | null): string {
  if (!reference) return "—";
  const at = reference.indexOf("@sha256:");
  if (at === -1) return reference;
  return `${reference.slice(0, at)}@sha256:${reference.slice(at + 8, at + 15)}…`;
}

/**
 * A calendar day, in UTC.
 *
 * For a figure that belongs to a day rather than to a moment: a day of metered
 * usage is a day, and rendering it with absoluteTime showed "1 Oct 2026, 02:00"
 * — a time of day that means nothing, and an hour that is the reader's offset
 * from UTC rather than anything about the usage.
 */
export function utcDay(iso: string | undefined | null): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat(undefined, {
    weekday: "short", day: "numeric", month: "short", timeZone: "UTC",
  }).format(date);
}

/**
 * A billing period, named the way a person names a month.
 *
 * # WHY NOT absoluteTime ON BOTH ENDS
 *
 * Because the period is a month in UTC and absoluteTime renders an instant in
 * the reader's own zone, with the time of day. In Berlin that turned "October"
 * into "Oct 1, 2026, 2:00 AM to Nov 1, 2026, 1:00 AM" — two in the morning
 * because of the offset, one in the morning because the clocks go back inside
 * the period. It was correct to the second and told nobody which month they
 * were looking at.
 *
 * The end is exclusive, so the last day shown is the day before it.
 */
export function billingPeriod(start: string, end: string): string {
  const from = new Date(start);
  const to = new Date(new Date(end).getTime() - 1);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return "—";

  const month = new Intl.DateTimeFormat(undefined, { month: "long", year: "numeric", timeZone: "UTC" });
  const day = new Intl.DateTimeFormat(undefined, { day: "numeric", month: "short", timeZone: "UTC" });

  // A whole calendar month is said as the month. Anything else — a first
  // period that began mid-month, or one carrying hours from an earlier one —
  // is said as the span it actually covers.
  const wholeMonth =
    from.getUTCDate() === 1 &&
    to.getUTCMonth() === from.getUTCMonth() &&
    to.getUTCFullYear() === from.getUTCFullYear() &&
    to.getUTCDate() === new Date(Date.UTC(from.getUTCFullYear(), from.getUTCMonth() + 1, 0)).getUTCDate();

  return wholeMonth ? month.format(from) : `${day.format(from)} to ${day.format(to)}`;
}
